/* eslint-disable filenames/match-regex */

import { CTA_URL_USAGE } from '@/consts.js';
import AppService from '@/services/App';
import { convertOldCampaignObjectiveToOdax } from '@/utils/facebook.js';

const availableLanguages = {
  'en': 'English',
  'hu': 'Magyar',
};

let language = localStorage.getItem('language');
if (!language) {
  language = (navigator.language || navigator.userLanguage).split('-')[0];
}
if (!Object.keys(availableLanguages).includes(language)) {
  language = 'en';
}
window.localStorage.setItem('language', language);

let theme = localStorage.getItem('theme');
if (!theme) {
  theme = 'light';
}
window.localStorage.setItem('theme', theme);
document.body.parentNode.dataset.theme = theme;

const state = {
  theme,
  isConnecting: false,
  isLoading: true,
  fbSdkLoaded: false,
  igSdkLoaded: false,
  availableLanguages,
  language,
  fields: [],
  fieldComparisons: [],
  countries: [],
  currencies: [],
  devices: [],
  mobileDevices: [],
  budgetPeriods: [],
  campaignNameMacros: [],
  adsetNameMacros: [],
  urlParamMacros: [],
  facebook: {},
  facebookOdaxMap: [],
  facebookAdAccountStatuses: [],
  facebookAdFormats: [],
  facebookLanguages: [],
  facebookCampaignObjectives: [],
  facebookCampaignBidStrategies: [],
  facebookAdsetBidStrategies: [],
  facebookSpecialAdCategories: [],
  facebookPostTypes: [],
  facebookPostMediaTypes: [],
  facebookAdsetOptimizationGoals: [],
  facebookAdsetBillingEvents: [],
  facebookAttributionWindows: [],
  facebookApCreativeFeatures: [],
  facebookCallToActions: [],
  facebookConversionEvents: [],
  instagramPostTypes: [],
  instagramPostMediaTypes: [],
  timezones: [],
  conversionLocations: [],
  facebookAdPlacements: {},
  dashboardMetrics: [],
  postMetrics: [],
  languagesISO: [],
  magicTool: {},
  industries: [],
  tones: [],
  fontFamilies: [],
};

const getters = {
  currentTheme(state) {
    return state.theme;
  },
  isLoading(state) {
    return state.isLoading;
  },
  isConnecting(state) {
    return state.isConnecting;
  },
  fbSdkLoaded(state) {
    return state.fbSdkLoaded;
  },
  availableLanguages(state) {
    return state.availableLanguages;
  },
  language(state) {
    return state.language;
  },
  fields(state) {
    return state.fields;
  },
  fieldComparisons(state) {
    return state.fieldComparisons;
  },
  campaignNameMacros(state) {
    return state.campaignNameMacros;
  },
  budgetPeriods(state) {
    return state.budgetPeriods;
  },
  countries(state) {
    return state.countries;
  },
  currencies(state) {
    return state.currencies;
  },
  facebook(state) {
    return state.facebook;
  },
  facebookOdaxMap(state) {
    return state.facebookOdaxMap;
  },
  facebookAdAccountStatuses(state) {
    return state.facebookAdAccountStatuses;
  },
  facebookAdFormats(state) {
    return state.facebookAdFormats;
  },
  facebookLanguages(state) {
    return state.facebookLanguages;
  },
  facebookCampaignObjectives(state) {
    return state.facebookCampaignObjectives;
  },
  facebookCampaignObjectiveLabel: state => objective => {
    const convertedObjective = convertOldCampaignObjectiveToOdax(objective);
    const found = state.facebookOdaxMap.find(item => item.machine_name === convertedObjective);
    if (found && found.label) {
      return found.label;
    }

    return convertedObjective;
  },
  facebookCampaignBidStrategies(state) {
    return state.facebookCampaignBidStrategies;
  },
  facebookAdsetBidStrategies(state) {
    return state.facebookAdsetBidStrategies;
  },
  facebookSpecialAdCategories(state) {
    return state.facebookSpecialAdCategories;
  },
  facebookPostTypes(state) {
    return state.facebookPostTypes;
  },
  facebookPostMediaTypes(state) {
    return state.facebookPostMediaTypes;
  },
  instagramPostTypes(state) {
    return state.instagramPostTypes;
  },
  instagramPostMediaTypes(state) {
    return state.instagramPostMediaTypes;
  },
  facebookConversionEvents(state) {
    return state.facebookConversionEvents;
  },
  facebookAdPlacements(state) {
    return state.facebookAdPlacements;
  },
  facebookCallToActions(state) {
    return state.facebookCallToActions;
  },
  facebookCallToActionsByOdax(state, getters) {
    return ({
      campaignObjective,
      conversionLocation,
      optimizationGoal,
    }) => {
      if (!Array.isArray(state.facebookCallToActions) || !state.facebookCallToActions.length) {
        return [];
      }

      // App destinations reject NO_BUTTON together with the store URL (1815791). Applied before the
      // early returns below, so it also holds when the destination or the goal is not known yet.
      let all = state.facebookCallToActions;
      if (getters.facebookOdaxCtaUrlUsage({ campaignObjective, conversionLocation }) === CTA_URL_USAGE.APP_STORE) {
        all = all.filter(cta => cta.machine_name !== 'NO_BUTTON');
      }

      if (!Array.isArray(state.facebookOdaxMap) || !state.facebookOdaxMap.length) {
        return all;
      }

      if (!campaignObjective || !conversionLocation || !optimizationGoal) {
        return all;
      }

      const objective = state.facebookOdaxMap.find(obj => obj.machine_name === campaignObjective);
      if (!objective?.destination_types?.length) {
        return all;
      }

      const destType = objective.destination_types.find(d => d.machine_name === conversionLocation);
      if (!destType?.optimization_goals?.length) {
        return all;
      }

      const goal = destType.optimization_goals.find(g => g.machine_name === optimizationGoal);
      const allowed = goal?.allowed_cta_buttons;

      if (!Array.isArray(allowed) || !allowed.length) {
        return all;
      }

      const filtered = all.filter(cta => allowed.includes(cta.machine_name));

      return filtered.length ? filtered : all;
    };
  },
  facebookOdaxCtaUrlUsage(state) {
    return ({ campaignObjective, conversionLocation }) => {
      const objective = state.facebookOdaxMap.find(obj => obj.machine_name === convertOldCampaignObjectiveToOdax(campaignObjective));
      if (!objective?.destination_types?.length) {
        return CTA_URL_USAGE.OPTIONAL;
      }

      const destType = objective.destination_types.find(d => d.machine_name === conversionLocation)
        ?? objective.destination_types[0];

      return destType.cta_url_usage ?? CTA_URL_USAGE.OPTIONAL;
    };
  },
  facebookApCreativeFeatures(state) {
    return state.facebookApCreativeFeatures;
  },
  adsetNameMacros(state) {
    return state.adsetNameMacros;
  },
  urlParamMacros(state) {
    return state.urlParamMacros;
  },
  facebookAdsetOptimizationGoals(state) {
    return state.facebookAdsetOptimizationGoals;
  },
  facebookAdsetBillingEvents(state) {
    return state.facebookAdsetBillingEvents;
  },
  facebookAttributionWindows(state) {
    return state.facebookAttributionWindows;
  },
  devices(state) {
    return state.devices;
  },
  mobileDevices(state) {
    return state.mobileDevices;
  },
  conversionLocations(state) {
    return state.conversionLocations;
  },
  timezones(state) {
    return state.timezones;
  },
  dashboardMetrics(state) {
    return state.dashboardMetrics;
  },
  postMetrics(state) {
    return state.postMetrics;
  },
  languagesISO(state) {
    return state.languagesISO;
  },
  magicTool(state) {
    return state.magicTool;
  },
  magicToolContentGeneration(state) {
    return state.magicTool.content_generation || {};
  },
  magicToolContentGenerationGoals(state) {
    return state.magicTool.content_generation?.goals || [];
  },
  industries(state) {
    return state.industries;
  },
  tones(state) {
    return state.tones;
  },
  fontFamilies(state) {
    return state.fontFamilies;
  },
};

const actions = {
  fetch(store) {
    return AppService.getConfig().then(result => {
      const propertyMapping = {
        fields: 'fields',
        field_comparisons: 'fieldComparisons',
        campaign_name_macros: 'campaignNameMacros',
        adset_name_macros: 'adsetNameMacros',
        url_param_macros: 'urlParamMacros',
        budget_periods: 'budgetPeriods',
        countries: 'countries',
        currencies: 'currencies',
        facebook: 'facebook',
        facebook_odax_map: 'facebookOdaxMap',
        facebook_ad_account_statuses: 'facebookAdAccountStatuses',
        facebook_ad_formats: 'facebookAdFormats',
        facebook_languages: 'facebookLanguages',
        facebook_campaign_objectives: 'facebookCampaignObjectives',
        facebook_campaign_bid_strategies: 'facebookCampaignBidStrategies',
        facebook_adset_bid_strategies: 'facebookAdsetBidStrategies',
        facebook_special_ad_categories: 'facebookSpecialAdCategories',
        facebook_adset_optimization_goals: 'facebookAdsetOptimizationGoals',
        facebook_adset_billing_events: 'facebookAdsetBillingEvents',
        facebook_attribution_windows: 'facebookAttributionWindows',
        facebook_ad_placements: 'facebookAdPlacements',
        facebook_call_to_actions: 'facebookCallToActions',
        facebook_conversion_events: 'facebookConversionEvents',
        facebook_post_types: 'facebookPostTypes',
        facebook_post_media_types: 'facebookPostMediaTypes',
        facebook_ap_creative_features: 'facebookApCreativeFeatures',
        instagram_post_types: 'instagramPostTypes',
        instagram_post_media_types: 'instagramPostMediaTypes',
        devices: 'devices',
        mobile_devices: 'mobileDevices',
        conversion_locations: 'conversionLocations',
        timezones: 'timezones',
        dashboard_metrics: 'dashboardMetrics',
        post_metrics: 'postMetrics',
        languages: 'languagesISO',
        magic_tool: 'magicTool',
        industries: 'industries',
        tones: 'tones',
        font_families: 'fontFamilies',
      };
      Object.entries(result).forEach(([ property, items ]) => {
        if (property in propertyMapping) {
          store.commit('updateState', {
            key: propertyMapping[property],
            items,
          });
        }
      });

      store.commit('isLoading', false);

      return result;
    });
  },
  loadFbSdk(store) {
    return new Promise(resolve => {
      if (store.getters['fbSdkLoaded']) {
        resolve();
        return;
      }

      store.commit('setFbSdkLoaded', true);

      /* global FB */
      window.fbAsyncInit = function() {
        FB.init({
          appId: store.getters['facebook'].app_id,
          version: 'v' + store.getters['facebook'].graph_version,
          xfbml: true,
        });

        resolve();
      };

      const scriptEle = document.createElement('script');
      scriptEle.setAttribute(
        'src',
        'https://connect.facebook.net/en_US/sdk.js',
      );
      scriptEle.setAttribute('type', 'text/javascript');
      scriptEle.setAttribute('async', true);
      scriptEle.setAttribute('defer', true);
      document.body.appendChild(scriptEle);
    });
  },
  loadIgSdk(store) {
    return new Promise(resolve => {
      if (store.getters['igSdkLoaded']) {
        resolve();
        return;
      }

      store.commit('setIgSdkLoaded', true);

      const scriptEle = document.createElement('script');
      scriptEle.setAttribute(
        'src',
        'https://platform.instagram.com/en_US/embeds.js',
      );
      scriptEle.setAttribute('type', 'text/javascript');
      scriptEle.setAttribute('async', true);
      scriptEle.onload = () => {
        resolve();
      };

      document.body.appendChild(scriptEle);
    });
  },
};

const mutations = {
  updateTheme(state, value) {
    state.theme = value;
    window.localStorage.setItem('theme', state.theme);
    document.body.parentNode.dataset.theme = state.theme;
  },
  isLoading(state, value) {
    state.isLoading = value;
  },
  isConnecting(state, value) {
    state.isConnecting = value;
  },
  setFbSdkLoaded(state, value) {
    state.fbSdkLoaded = value;
  },
  setIgSdkLoaded(state, value) {
    state.igSdkLoaded = value;
  },
  language(state, value) {
    state.language = value;
    window.localStorage.setItem('language', state.language);
  },
  updateState(state, { key, items }) {
    state[key] = items;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
